import { useState, useEffect, ReactNode } from 'react';
import logo from '@/assets/logo-full.png';

interface GeoRestrictionProps {
  children: ReactNode;
}

const GeoRestriction = ({ children }: GeoRestrictionProps) => {
  const [isAllowed, setIsAllowed] = useState<boolean | null>(null);

  useEffect(() => {
    const checkCountry = async () => {
      // Check cache first
      const cached = sessionStorage.getItem('user_country');
      if (cached) {
        setIsAllowed(cached === 'GB');
        return;
      }

      try {
        const response = await fetch('https://ipapi.co/country_code/');
        if (!response.ok) throw new Error('API failed');
        const countryCode = await response.text();
        sessionStorage.setItem('user_country', countryCode.trim());
        setIsAllowed(countryCode.trim() === 'GB');
      } catch {
        // Fail-open: allow access if API fails
        sessionStorage.setItem('user_country', 'GB');
        setIsAllowed(true);
      }
    };

    checkCountry();
  }, []);

  // Show loading state while checking
  if (isAllowed === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse">
          <img src={logo} alt="PTA Hub" className="h-12 opacity-50" />
        </div>
      </div>
    );
  }

  // Show restricted message for non-UK visitors
  if (!isAllowed) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background px-4">
        <div className="max-w-md text-center space-y-6">
          <img src={logo} alt="PTA Hub" className="h-16 mx-auto" />
          <h1 className="text-2xl font-bold text-foreground">
            UK Only Service
          </h1>
          <p className="text-muted-foreground">
            PTA Hub is currently only available to users in the United Kingdom. 
            We appreciate your interest and hope to expand to other regions in the future.
          </p>
          <p className="text-sm text-muted-foreground">
            If you believe you're seeing this message in error, please{' '}
            <a href="mailto:support@ptahub.co.uk" className="text-primary hover:underline">
              contact us
            </a>
            .
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default GeoRestriction;
